/**
 * Local-calendar date helpers for the booking date pickers.
 *
 * The booking UI works with date-only strings (`YYYY-MM-DD`) that represent a
 * *calendar day* the user picked — they have no time and no timezone. The trap
 * is the standard library:
 *
 *   - `date.toISOString()` converts to **UTC** first. For a user ahead of UTC
 *     (Morocco is UTC+1) local midnight rolls back to the previous UTC day, so
 *     a chip showing "30" would serialize to "...-29".
 *   - `new Date("2026-06-30")` parses as **UTC midnight**, which renders as the
 *     previous day for users behind UTC.
 *
 * Both bugs are timezone-direction-dependent and silent. These helpers stay in
 * local time end-to-end so the day the user taps is the day that is stored,
 * formatted, and sent to the API — in every timezone.
 */

/** Serialize a Date to `YYYY-MM-DD` using its LOCAL calendar parts (never UTC). */
export function toLocalISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse a `YYYY-MM-DD` string to a Date at LOCAL midnight (never UTC). */
export function parseLocalISO(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Add (or subtract) whole days to a `YYYY-MM-DD` string, staying in local time. */
export function addDaysISO(iso: string, days: number): string {
  const d = parseLocalISO(iso);
  d.setDate(d.getDate() + days);
  return toLocalISO(d);
}

/**
 * A booking's calendar day ("2026-05-01", or an ISO timestamp whose first ten
 * characters are that day) as a Date at LOCAL noon. Noon, not midnight, so no
 * timezone on earth can roll it onto a neighbouring day: `new Date("2026-05-01")`
 * is UTC midnight, which a guest in New York reads as 30 April.
 */
export function calendarDayAtNoon(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * "1 May 2026" / "1 mai 2026" / "1 مايو 2026": the booked day in the page's
 * language, Latin digits in every locale (ar included). Falls back to the raw
 * string when it is not a date, and to a dash when it is empty.
 */
export function formatCalendarDay(value: string, lang: string): string {
  if (!value) return "—";
  const d = calendarDayAtNoon(value);
  if (!d) return value;
  try {
    return new Intl.DateTimeFormat(`${lang || "en"}-u-nu-latn`, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(d);
  } catch {
    return value.slice(0, 10);
  }
}
